/**
 * useIdleDetection — Returns true when user has been idle for `ms` milliseconds.
 *
 * Tracks mousemove, keydown, scroll, touchstart events. Resets on any activity.
 *
 * @module @humanizer/studio/hooks/useIdleDetection
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export function useIdleDetection(ms: number): boolean {
  const [isIdle, setIsIdle] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    setIsIdle(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setIsIdle(true), ms);
  }, [ms]);

  useEffect(() => {
    const events: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'scroll', 'touchstart'];
    for (const event of events) {
      window.addEventListener(event, resetTimer, { passive: true });
    }
    // Start the initial timer
    timerRef.current = setTimeout(() => setIsIdle(true), ms);

    return () => {
      for (const event of events) {
        window.removeEventListener(event, resetTimer);
      }
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [ms, resetTimer]);

  return isIdle;
}
